import { InputHTMLAttributes, useEffect, useRef, useState } from 'react';
import { CrossIcon } from '../../../Icons';
import styles from './styles.module.scss';

type Props = {
  id?: string;
  name?: string;
  value?: File;
  onChange?: (file?: File) => void;
  onBlur?: InputHTMLAttributes<HTMLInputElement>['onBlur'];
  accept?: string;
  placeholder?: string;
  width?: number;
  height?: number;
  /** 初期表示用の画像URL（編集画面などで既存画像を表示する場合に使用） */
  defaultPreviewUrl?: string;
  /** 画像削除時のコールバック */
  onRemove?: () => void;
};

export const ImageInput: React.FC<Props> = ({
  id,
  name,
  value,
  onChange,
  onBlur,
  accept = 'image/*',
  placeholder = '画像をアップロード',
  width = 200,
  height = 200,
  defaultPreviewUrl,
  onRemove,
}) => {
  const internalRef = useRef<HTMLInputElement>(null);
  const isRemovedRef = useRef<boolean>(false);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(defaultPreviewUrl);

  useEffect(() => {
    if (value) {
      const url = URL.createObjectURL(value);
      setPreviewUrl(url);
      isRemovedRef.current = false;
      return () => URL.revokeObjectURL(url);
    }
    // ユーザーが明示的に削除した場合はdefaultPreviewUrlに戻さない
    if (!isRemovedRef.current) {
      setPreviewUrl(defaultPreviewUrl);
    }
  }, [value, defaultPreviewUrl]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    onChange?.(file);
  };

  const handleRemove = () => {
    if (internalRef.current !== null) {
      internalRef.current.value = '';
    }
    isRemovedRef.current = true;
    setPreviewUrl(undefined);
    onRemove?.();
    onChange?.(undefined);
  };

  const containerStyle = {
    '--image-input-width': `${width}px`,
    '--image-input-height': `${height}px`,
  } as React.CSSProperties;

  return (
    <div className={styles.ImageInput} style={containerStyle}>
      {previewUrl ? (
        <div className={styles.ImageInput__PreviewContainer}>
          <img src={previewUrl} alt='Preview' className={styles.ImageInput__PreviewImage} />
          <button
            type='button'
            onClick={handleRemove}
            className={styles.ImageInput__RemoveButton}
            aria-label='画像を削除'
          >
            <CrossIcon />
          </button>
        </div>
      ) : (
        <label htmlFor={id} className={styles.ImageInput__UploadLabel}>
          <span className={styles.ImageInput__PlaceholderText}>{placeholder}</span>
        </label>
      )}
      <input
        ref={internalRef}
        type='file'
        id={id}
        name={name}
        accept={accept}
        onChange={handleFileChange}
        onBlur={onBlur}
        className={styles.ImageInput__FileInput}
      />
    </div>
  );
};
